import { useState, useRef } from "react";
import { useResponsive } from "../../hooks/useResponsive";
import { GuestMenu } from "./GuestMenu";
import { AccountMenu } from "../../features/auth/AccountMenu";
import { T } from "../../i18n/translations.js";

export function CHSLogo({ height = 42 }: { height?: number }) {
  const width = Math.round(height * (400 / 160));
  return (
    <img
      src="/logo.png"
      alt="CHASS1S"
      width={width}
      height={height}
      style={{ width, height, display: "block", imageRendering: "auto" }}
    />
  );
}

export function AppHeader({ lang, setLang, children = null, user, profile, onOpenAuth, onSignOut, onRefreshProfile,
  workspaces, currentWorkspace, onSwitchWorkspace, onCreateWorkspace, onWorkspaceDeleted, onOpenHistory, onOpenAdmin,
  onOpenSettings, onOpenSupport, chassisCount = 0, recentChassisNames = [], onLogoClick }: any) {
  const { isMobile } = useResponsive();
  const [menuOpen, setMenuOpen] = useState(false);
  const authRef = useRef(null);
  const initials = user ? (() => {
    const rawSrc = profile?.display_name?.trim() || user.email || '?';
    const words = rawSrc.split(/\s+/).filter(Boolean);
    return words.length >= 2
      ? (words[0][0] + words[1][0]).toUpperCase()
      : rawSrc.slice(0, 2).toUpperCase();
  })() : null;

  return (
    <div style={{ borderBottom: "1px solid #e0e0e0", background: "#fff", padding: isMobile ? "0 16px" : "0 40px", position: "sticky", top: 0, zIndex: 100 }}>
      <div style={{ maxWidth: 1100, margin: "0 auto", display: "flex", alignItems: "flex-start", justifyContent: "space-between", padding: "0 0 12px 0", flexWrap: "nowrap", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, overflow: "hidden" }}>
          <button onClick={() => onLogoClick ? onLogoClick() : window.scrollTo({ top: 0, behavior: "smooth" })} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", display: "block" }}>
            <CHSLogo height={isMobile ? 37 : 56} />
          </button>
          <div style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0, alignSelf: "center" }}>
            <span style={{ fontFamily: "'Courier New', monospace", fontSize: 11, color: "#111", letterSpacing: "0.08em", fontWeight: 700, whiteSpace: "nowrap" }}>BO11Y FRAMEWORK</span>
            <a href="http://boss.technology" target="_blank" rel="noopener noreferrer" style={{ fontFamily: "'Courier New', monospace", fontSize: isMobile ? 9 : 10, color: "#888", letterSpacing: "0.06em", fontWeight: 700, textDecoration: "none" }}>a Boss.Technology</a>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 10 : 16, flexShrink: 0, alignSelf: "center" }}>
          {children}
          {currentWorkspace && (
            <span title={currentWorkspace.name}
              style={{ fontFamily: "'Courier New', monospace", fontSize: 10,
                fontWeight: 700, color: "#888", padding: "3px 10px",
                border: "1px solid #d0d0d0", borderRadius: 2,
                maxWidth: isMobile ? 90 : 180, overflow: "hidden",
                textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {currentWorkspace.name}
            </span>
          )}
          {profile?.role === "admin" && onOpenAdmin && (
            <button onClick={onOpenAdmin}
              title="Admin Panel"
              style={{ fontFamily: "'Courier New', monospace", fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", padding: "5px 12px", background: "#000", color: "#fff", border: "none", cursor: "pointer", textTransform: "uppercase" }}>
              ADMIN
            </button>
          )}
          <div ref={authRef} style={{ position: "relative" }}>
            {!user ? (
              <button onClick={() => setMenuOpen(o => !o)}
                title="Language & sign in"
                style={{ display: "flex", alignItems: "center", justifyContent: "center",
                  width: 34, height: 34, borderRadius: "50%", border: "1px solid #d0d0d0",
                  background: menuOpen ? "#000" : "#fff", cursor: "pointer", transition: "all 0.15s" }}
                onMouseEnter={e => { (e.currentTarget as any).style.background = "#000"; (e.currentTarget.querySelector("svg") as any).style.stroke = "#fff"; }}
                onMouseLeave={e => { if (!menuOpen) { (e.currentTarget as any).style.background = "#fff"; (e.currentTarget.querySelector("svg") as any).style.stroke = "#888"; } }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                  stroke={menuOpen ? "#fff" : "#888"} strokeWidth="2" strokeLinecap="round"
                  strokeLinejoin="round" style={{ transition: "stroke 0.15s" }}>
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
              </button>
            ) : (
              <button onClick={() => setMenuOpen(o => !o)}
                title={user.email}
                style={{ display: "flex", alignItems: "center", gap: 8, background: "transparent",
                  border: "1px solid #d0d0d0", padding: "4px 10px 4px 6px", cursor: "pointer",
                  borderRadius: 2, transition: "all 0.15s" }}
                onMouseEnter={e => (e.currentTarget as any).style.borderColor = "#000"}
                onMouseLeave={e => (e.currentTarget as any).style.borderColor = "#d0d0d0"}>
                <div style={{ width: 24, height: 24, borderRadius: "50%", background: "#000",
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <span style={{ fontFamily: "'Courier New', monospace", fontSize: 9,
                    fontWeight: 900, color: "#fff", lineHeight: 1 }}>{initials}</span>
                </div>
              </button>
            )}
            {menuOpen && !user && (
              <GuestMenu
                lang={lang} setLang={setLang}
                onOpenAuth={(mode: string) => { onOpenAuth(mode); setMenuOpen(false); }}
                onClose={() => setMenuOpen(false)}
              />
            )}
            {menuOpen && user && (
              <AccountMenu
                user={user} profile={profile} lang={lang} setLang={setLang}
                onSignOut={onSignOut} onRefreshProfile={onRefreshProfile}
                workspaces={workspaces || []} currentWorkspace={currentWorkspace}
                onSwitchWorkspace={onSwitchWorkspace} onCreateWorkspace={onCreateWorkspace}
                onWorkspaceDeleted={onWorkspaceDeleted}
                onOpenHistory={() => { onOpenHistory(); setMenuOpen(false); }}
                onOpenSettings={() => { onOpenSettings?.(); setMenuOpen(false); }}
                onOpenSupport={() => { onOpenSupport?.(); setMenuOpen(false); }}
                onClose={() => setMenuOpen(false)}
                t={(T as any)[lang]} chassisCount={chassisCount} recentChassisNames={recentChassisNames}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function AppFooter({ t }: { t: any }) {
  const { isMobile } = useResponsive();
  return (
    <div style={{ borderTop: "3px solid #000", background: "#000", padding: isMobile ? "14px 16px" : "18px 40px" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto", textAlign: "center" }}>
        <span style={{ fontFamily: "'Courier New', monospace", fontSize: 11, color: "#888", letterSpacing: "0.1em" }}>©️ 2026 88 GREENWICH AVE LLC d/b/a CHASS1S</span>
      </div>
    </div>
  );
}
